import { Alert, Button, Loader, Tabs, Tooltip } from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBan,
  IconCircleCheck,
  IconPrinter,
  IconGitCompare,
  IconHistory,
  IconListTree,
} from "@tabler/icons-react";
import type {
  QuoteItemInput,
  RfqDetailData,
  RfqLineInput,
  RfqPartnerOption,
} from "@/lib/rfq/api";
import type {
  RfqAward,
  RfqAttachment,
  RfqLine,
  RfqRole,
} from "@/lib/rfq/types";
import {
  addRfqVendors,
  approveRfq,
  createRfqLines,
  deleteRfqLines,
  loadPartnerOptions,
  loadRfqDetail,
  rejectRfq,
  removeRfqVendor,
  rfqListPath,
  saveRfqAwards,
  saveVendorQuotes,
  setVendorStatus,
  submitRfqForReview,
  updateRfqHeader,
  updateRfqLine,
  uploadRfqAttachment,
} from "@/lib/rfq/api";
import type { RfqSheetLabels } from "@/lib/rfq/exportSheet";
import { buildRfqSheet, renderRfqSheetHtml } from "@/lib/rfq/exportSheet";
import SignPrintPreview from "@/components/SignPrintPreview";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import RfqDetailTab from "@/components/rfq/RfqDetailTab";
import type { RfqHeaderPatch } from "@/components/rfq/RfqInfoCard";
import RfqHistoryTab from "@/components/rfq/RfqHistoryTab";
import RfqInfoCard from "@/components/rfq/RfqInfoCard";
import RfqPricingTab from "@/components/rfq/RfqPricingTab";
import RfqStatusBadge from "@/components/rfq/RfqStatusBadge";
import RfqVendorTab from "@/components/rfq/RfqVendorTab";
import { authClient } from "@/lib/auth-clients";
import { buildHistoryEvents } from "@/lib/rfq/history";
import { orpcClient } from "@/lib/orpc-client";
import { useRfqLabels } from "@/lib/rfq/labels";

const MANAGER_ROLES = ["owner", "admin", "purchaseManager"];

const RFQ_PRINT_MARGIN = { topMm: 12, sideMm: 10 };

function RfqPage() {
  const { t } = useRfqLabels();
  const { id } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState<RfqRole>("buyer");
  const [data, setData] = useState<RfqDetailData | null>(null);
  const [partners, setPartners] = useState<RfqPartnerOption[]>([]);
  const [awards, setAwards] = useState<RfqAward[]>([]);
  const [tab, setTab] = useState<string | null>("detail");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const result = await authClient.organization.getActiveMember();
        if (result?.data?.role) {
          const roles = (result.data.role as string)
            .split(",")
            .map((value: string) => value.trim())
            .filter(Boolean);
          if (roles.some((value) => MANAGER_ROLES.indexOf(value) >= 0)) {
            setRole("manager");
          }
        }
      } catch (fetchError) {
        console.error("Error fetching user roles:", fetchError);
      }
    };

    fetchUserRoles();
  }, []);

  const reload = useCallback(async () => {
    if (!id) return;
    setError("");
    try {
      const detail = await loadRfqDetail(id);
      setData(detail);
      setAwards(detail.awards);
    } catch (loadError: any) {
      setError(loadError?.message || t("rfq.loadFailed") || "載入詢價單失敗");
    }
  }, [id, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([reload(), loadPartnerOptions()])
      .then(([, partnerOptions]) => {
        if (!cancelled) setPartners(partnerOptions ?? []);
      })
      .catch((loadError: any) => {
        if (!cancelled)
          setError(loadError?.message || t("rfq.loadFailed") || "載入詢價單失敗");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload, t]);

  useEffect(() => {
    (orpcClient.company.read({}) as Promise<any>)
      .then((company) => setCompanyName(company?.name ?? ""))
      .catch((fetchError) => console.error("Error fetching company:", fetchError));
  }, []);

  const sheetLabels = useMemo<RfqSheetLabels>(
    () => ({
      title: t("rfq.export.title") || "詢價單",
      purchaseTitle: t("rfq.export.purchaseTitle") || "採購單",
      projectAddress: t("rfq.export.projectAddress") || "工程地址",
      projectName: t("rfq.export.projectName") || "工程案名",
      code: t("rfq.export.code") || "詢價單號",
      printedAt: t("rfq.export.printedAt") || "製表日期",
      item: t("rfq.export.item") || "項目",
      vendor: t("rfq.export.vendor") || "廠商",
      spec: t("rfq.export.spec") || "規格",
      quotePrice: t("rfq.export.quotePrice") || "詢價單價",
      quoteDate: t("rfq.export.quoteDate") || "詢價日期",
      negotiatedPrice: t("rfq.export.negotiatedPrice") || "議價單價",
      negotiatedDate: t("rfq.export.negotiatedDate") || "議價日期",
      purchasePrice: t("rfq.export.purchasePrice") || "採購單價",
      purchaseDate: t("rfq.export.purchaseDate") || "採購日期",
      remark: t("rfq.export.remark") || "備註",
      contractQty: t("rfq.export.contractQty") || "合約數量",
      costPrice: t("rfq.export.costPrice") || "成本單價",
      customerPrice: t("rfq.export.customerPrice") || "客戶單價",
      handler: t("rfq.export.handler") || "經辦",
      manager: t("rfq.export.manager") || "經理",
      vicePresident: t("rfq.export.vicePresident") || "副總",
      declined: t("rfq.export.declined") || "婉拒報價",
      notPurchased: t("rfq.export.notPurchased") || "不採購",
    }),
    [t],
  );

  const [printHtml, setPrintHtml] = useState<string | null>(null);

  const handlePrintSheet = () => {
    if (data) setPrintHtml(renderRfqSheetHtml(buildRfqSheet(data, companyName, sheetLabels), sheetLabels));
  };

  const run = useCallback(
    async (action: () => Promise<void>) => {
      setError("");
      try {
        await action();
        await reload();
      } catch (actionError: any) {
        setError(actionError?.message || t("rfq.actionFailed") || "操作失敗");
      }
    },
    [reload, t],
  );

  const header = data?.header;
  const lines = data?.lines ?? [];
  const vendors = data?.vendors ?? [];
  const quotes = data?.quotes ?? [];
  const editable = header?.serverStatus === "inquiring";

  const history = useMemo(() => {
    if (!data) return [];
    return buildHistoryEvents(data.historyRows, {
      lines: data.lines,
      vendors: data.vendors,
      employeeNames: data.employeeNames,
      t,
      systemActor: t("rfq.history.system") || "系統",
    });
  }, [data, t]);

  const handleSaveHeader = (patch: RfqHeaderPatch) =>
    run(async () => {
      if (!id || Object.keys(patch).length === 0) return;
      await updateRfqHeader(id, patch);
    });

  const handleAttachments = (attachments: RfqAttachment[]) =>
    run(async () => {
      if (!id) return;
      await updateRfqHeader(id, { attachments });
    });

  const handleCreateLines = (
    inputs: Omit<RfqLineInput, "seq">[],
    sourceOrderIds?: string[],
  ) =>
    run(async () => {
      if (!id) return;
      const start = lines.reduce((max, line) => Math.max(max, line.seq), 0);
      await createRfqLines(
        id,
        inputs.map((input, index) => ({ ...input, seq: start + index + 1 })),
      );
      if (sourceOrderIds?.length && header) {
        const merged = Array.from(
          new Set([...header.sourceOrderIds, ...sourceOrderIds]),
        );
        if (merged.length !== header.sourceOrderIds.length) {
          await updateRfqHeader(id, { sourceOrderIds: merged });
        }
      }
    });

  const handleUpdateLine = (lineId: string, patch: Partial<RfqLine>) =>
    run(() => updateRfqLine(lineId, patch));

  const handleDeleteLines = (ids: string[]) => run(() => deleteRfqLines(ids));

  const handleAddVendors = (partnerIds: string[]) =>
    run(async () => {
      if (!id) return;
      await addRfqVendors(id, partnerIds);
    });

  const handleDecline = (vendorId: string) =>
    run(() => setVendorStatus(vendorId, "declined"));

  const handleRemoveVendor = (vendorId: string) =>
    run(() => removeRfqVendor(vendorId));

  const handleRecord = (vendorId: string, rows: QuoteItemInput[]) =>
    run(() => saveVendorQuotes(vendorId, rows));

  const handleSaveAwards = (next: RfqAward[]) =>
    run(() => saveRfqAwards(next));

  const handleSubmitForReview = () =>
    run(async () => {
      if (!id) return;
      await submitRfqForReview(id);
    });

  const handleApprove = () =>
    run(async () => {
      if (!id) return;
      await approveRfq(id);
    });

  const handleReject = (reason: string) =>
    run(async () => {
      if (!id) return;
      await rejectRfq(id, reason);
    });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!header) {
    return (
      <div className="p-8">
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {error || t("rfq.notFound") || "找不到此詢價單"}
        </Alert>
        <Button
          className="mt-4"
          variant="default"
          radius="md"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate(rfqListPath(header?.serverStatus))}
        >
          {t("rfq.backToList") || "回詢價單列表"}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              radius="md"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate(rfqListPath(header?.serverStatus))}
            >
              {t("rfq.backToList") || "回詢價單列表"}
            </Button>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("rfq.title") || "詢價單"}
            </div>
            <RfqStatusBadge status={header.status} />
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {role === "manager"
                ? t("rfq.roleManager") || "採購主管視角"
                : t("rfq.roleBuyer") || "採購承辦視角"}
            </span>
          </div>
        </div>

        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
          <span className="mr-auto text-sm text-gray-500">
            {editable
              ? t("rfq.statusNoticeInquiring") ||
                "詢價中 — 可調整主檔、明細、廠商與決標"
              : t("rfq.statusNoticeLocked") ||
                "非詢價中狀態 — 主檔、明細與決標唯讀"}
          </span>
          {header.serverStatus === "underReview" && role === "manager" ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <IconCircleCheck size={14} />
              {t("rfq.reviewHint") || "請至「廠商與比價」分頁核准或駁回"}
            </span>
          ) : null}
          {/* 已完成才開放列印 */}
          {header.serverStatus === "completed" ? (
            <Button
              size="xs"
              radius="md"
              variant="light"
              leftSection={<IconPrinter size={14} />}
              onClick={handlePrintSheet}
            >
              {t("rfq.export.print") || "列印"}
            </Button>
          ) : null}
          {/* <Tooltip
            label={t("rfq.voidDisabled") || "後端未提供作廢狀態，暫不支援"}
            withArrow
          >
            <span>
              <Button
                size="xs"
                radius="md"
                variant="light"
                color="red"
                disabled
                leftSection={<IconBan size={14} />}
              >
                {t("rfq.void") || "作廢"}
              </Button>
            </span>
          </Tooltip> */}
        </div>

        <RfqInfoCard
          header={header}
          lines={lines}
          role={role}
          editable={!!editable}
          onSave={handleSaveHeader}
          onAttachmentsChange={handleAttachments}
          onUploadAttachment={uploadRfqAttachment}
        />

        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="detail" leftSection={<IconListTree size={16} />}>
              {t("rfq.tabDetail") || "明細"}
              <span className="ml-1.5 rounded bg-gray-100 px-1.5 text-xs text-gray-500">
                {lines.length}
              </span>
            </Tabs.Tab>
            <Tabs.Tab
              value="pricing"
              leftSection={<IconGitCompare size={16} />}
            >
              {t("rfq.tabPricing") || "廠商與比價"}
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              {t("rfq.tabHistory") || "歷程"}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="detail" pt="md">
            <RfqDetailTab
              lines={lines}
              role={role}
              editable={!!editable}
              products={data?.products ?? []}
              onCreateLines={handleCreateLines}
              onUpdateLine={handleUpdateLine}
              onDeleteLines={handleDeleteLines}
            />
          </Tabs.Panel>
          <Tabs.Panel value="pricing" pt="md">
            <div className="flex flex-col gap-4">
              <RfqVendorTab
                vendors={vendors}
                lines={lines}
                quotes={quotes}
                partners={partners}
                editable={!!editable}
                onAddVendors={handleAddVendors}
                onDecline={handleDecline}
                onRemove={handleRemoveVendor}
                onRecord={handleRecord}
              />
              <RfqPricingTab
                lines={lines}
                role={role}
                vendors={vendors}
                quotes={quotes}
                awards={awards}
                onAwardsChange={setAwards}
                editable={!!editable}
                status={header.status}
                onSaveAwards={handleSaveAwards}
                onSubmitForReview={handleSubmitForReview}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="history" pt="md">
            <RfqHistoryTab events={history} />
          </Tabs.Panel>
        </Tabs>
      </div>

      <SignPrintPreview
        open={printHtml != null}
        title={`${sheetLabels.title} ${header.code ?? ""}`.trim()}
        html={printHtml ?? ""}
        margin={RFQ_PRINT_MARGIN}
        onClose={() => setPrintHtml(null)}
      />
    </div>
  );
}

export default RfqPage;
